import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Navigation } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Tabs } from "@/components/ui/Tabs";
import { DataTable } from "@/components/tables/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Map } from "@/components/maps/Map";
import { MapMarker } from "@/components/maps/MapMarker";
import { useLiveRides } from "@/features/rides/useRides";
import { useRideRealtimeSync } from "@/features/rides/useRideRealtimeSync";
import { RideDetailDrawer } from "@/features/rides/RideDetailDrawer";
import { useLiveDriverLocations } from "@/hooks/useLiveDriverLocations";
import { formatINR } from "@/utils/format";
import type { RideRow } from "@/types/domain";

// Kozhikode (Calicut) — matches the wireframe's default operating city.
const DEFAULT_CENTER: [number, number] = [11.2588, 75.7804];

const TABS = [
  { value: "all", label: "Ongoing" },
  { value: "searching", label: "Searching" },
  { value: "issues", label: "Issues" },
];

export default function LiveRides() {
  const [tab, setTab] = useState("all");
  const [selectedRideId, setSelectedRideId] = useState<string | null>(null);
  useRideRealtimeSync();

  const liveRides = useLiveRides();
  const { locations } = useLiveDriverLocations();

  const rows = useMemo(() => {
    const all = liveRides.data ?? [];
    if (tab === "searching") return all.filter((r) => r.status === "SEARCHING");
    if (tab === "issues") return all.filter((r) => r.alert);
    return all;
  }, [liveRides.data, tab]);

  const columns = useMemo<ColumnDef<RideRow, unknown>[]>(
    () => [
      { header: "Ride", accessorKey: "code" },
      { header: "Rider", accessorKey: "rider" },
      { header: "Driver", accessorKey: "driver" },
      {
        header: "₹/km",
        accessorFn: (r) => (r.perKm !== undefined ? `₹${r.perKm}` : "—"),
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge status={row.original.status} label={row.original.statusLabel} alert={row.original.alert} />
        ),
      },
      {
        header: "Fare",
        cell: ({ row }) => (row.original.fare !== undefined ? formatINR(row.original.fare) : "—"),
      },
      {
        header: "",
        id: "track",
        cell: () => (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600">
            <Navigation className="h-3.5 w-3.5" /> track
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title={`Active rides · ${liveRides.data?.length ?? 0}`}
      description="Monitor and intervene on active trips."
      actions={<Tabs items={TABS} value={tab} onChange={setTab} />}
    >
      <div className="card overflow-hidden">
        <Map center={DEFAULT_CENTER} zoom={12} height={320}>
          {locations.map((loc) => (
            <MapMarker key={loc.driverId} position={[loc.latitude, loc.longitude]} color="#0F6B5C">
              {loc.name ?? loc.driverId}
            </MapMarker>
          ))}
        </Map>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={rows}
          isLoading={liveRides.isLoading}
          error={liveRides.error}
          onRetry={() => liveRides.refetch()}
          onRowClick={(r) => setSelectedRideId(r.id)}
          rowKey={(r) => r.id}
          emptyTitle="No active rides right now"
        />
      </div>
      <p className="text-xs text-ink-faint">
        Click a ride to open its detail, timeline, and payment/refund actions.
      </p>

      <RideDetailDrawer rideId={selectedRideId} onClose={() => setSelectedRideId(null)} />
    </PageContainer>
  );
}
